/**
 * Preferenza "ultimo ristorante usato" — namespaced per utente.
 *
 * PERCHÉ PER-UTENTE:
 * la chiave era globale (`deskbay_last_restaurant_id`) e non veniva rimossa al
 * logout. Il login successivo con un altro account la rileggeva e la usava sia
 * per il prefetch ottimistico sia per risolvere il ristorante da aprire, cioè
 * ereditava il contesto dell'account precedente. Con una chiave per utente ogni
 * account ha la propria preferenza, nessuno può leggere quella di un altro e
 * non serve cancellare nulla al logout (che è la parte fragile: se la remove
 * fallisce silenziosamente il problema si ripresenta).
 */

import { storage } from './index';

const PREFIX = 'deskbay_last_restaurant_id';

/** Chiave storica, globale e condivisa fra tutti gli account sullo stesso device. */
const LEGACY_KEY = PREFIX;

const keyFor = (userId: string) => `${PREFIX}:${userId}`;

/** Legge la preferenza dell'utente indicato. Nessun fallback su altre chiavi. */
export async function getLastRestaurantId(userId: string): Promise<string | null> {
    if (!userId) return null;
    return storage.get(keyFor(userId));
}

export async function setLastRestaurantId(userId: string, restaurantId: string): Promise<void> {
    if (!userId || !restaurantId) return;
    await storage.set(keyFor(userId), restaurantId);
}

export async function clearLastRestaurantId(userId: string): Promise<void> {
    if (!userId) return;
    await storage.remove(keyFor(userId));
}

/**
 * Consuma la chiave globale pre-migrazione: la legge una volta e la rimuove.
 *
 * Il valore restituito NON è attribuibile ad alcun utente, quindi il chiamante
 * può usarlo solo dopo aver verificato che il ristorante appartenga davvero
 * all'utente corrente (cioè che sia presente in `availableRestaurants`) — mai
 * per un prefetch, che partirebbe prima di quella verifica.
 *
 * Serve solo a non far perdere la selezione corrente agli utenti multi-ristorante
 * al primo avvio dopo l'aggiornamento; dal secondo in poi ritorna sempre null.
 */
export async function consumeLegacyLastRestaurantId(): Promise<string | null> {
    const legacy = await storage.get(LEGACY_KEY);
    if (legacy) {
        await storage.remove(LEGACY_KEY);
    }
    return legacy;
}
